import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useClerk } from "@clerk/clerk-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { engagementApi, type PushPreference } from "@/api/client";
import { currentSubscription, disablePush, enablePush, pushSupported } from "@/lib/webPush";
import { setSoundOn, soundOn, chime } from "@/lib/celebrate.core";
import { isPostHogOptedOut, setPostHogOptOut } from "@/lib/posthog";
import { getViewAsUserId } from "@/lib/viewAs";

/****************
 * NOTIFICATIONS AND SOUNDS. 2026-10-21, engagement redesign.
 * The user chooses what reaches them (research: control over notifications
 * keeps people; generic reminders do not). Per kind, the daily recap's hour
 * in their own time, and the sound on wins. Same list on the phone's Settings.
 ****************/
export function NotificationSettings() {
  const { t } = useTranslation("common");
  const clerk = useClerk();
  const qc = useQueryClient();
  const token = async () => (await clerk.session?.getToken()) ?? "";
  const viewAs = !!getViewAsUserId();
  const [device, setDevice] = useState<"on" | "off" | "unsupported">(pushSupported() ? "off" : "unsupported");
  const [sound, setSound] = useState(soundOn());
  const [usage, setUsage] = useState(!isPostHogOptedOut());

  useEffect(() => {
    currentSubscription().then((s) => s && setDevice("on")).catch(() => {});
  }, []);

  const prefs = useQuery({
    queryKey: ["push-preferences"],
    queryFn: async () => (await engagementApi.pushPreferences(await token())).preferences,
    enabled: !viewAs,
    retry: 0,
  });
  const save = useMutation({
    mutationFn: async (p: PushPreference) => (await engagementApi.setPushPreference(await token(), p)).preferences,
    onSuccess: (list) => qc.setQueryData(["push-preferences"], list),
    onError: () => toast.error(t("notif.saveFailed")),
  });

  const offset = new Date().getTimezoneOffset() / 60; // hours behind UTC
  const toUtc = (local: number) => ((local + offset) % 24 + 24) % 24;
  const toLocal = (utc: number) => ((utc - offset) % 24 + 24) % 24;

  return (
    <div className="space-y-3 text-[13px]">
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0">{t("notif.device")}</span>
        {device === "unsupported" ? (
          <span className="text-muted-foreground whitespace-nowrap">{t("notif.unsupported")}</span>
        ) : (
          <Button size="sm" variant={device === "on" ? "outline" : "default"} disabled={viewAs}
            onClick={async () => {
              if (device === "on") {
                await disablePush(await token());
                setDevice("off");
                return;
              }
              const r = await enablePush(await token());
              if (r === "on") { setDevice("on"); toast.success(t("notif.enabled")); }
              else toast.error(r === "denied" ? t("notif.denied") : t("notif.unavailable"));
            }}>
            {device === "on" ? t("notif.turnOff") : t("notif.turnOn")}
          </Button>
        )}
      </div>

      {(prefs.data ?? []).map((p) => (
        <div key={p.kind} className="flex items-center justify-between gap-3">
          <span className="min-w-0 whitespace-nowrap overflow-hidden text-ellipsis">{t(`notif.kind.${p.kind}`)}</span>
          <div className="flex items-center gap-2 shrink-0">
            {p.kind === "dailyRecap" && p.enabled && (
              <select
                aria-label={t("notif.recapHour")}
                className="bg-transparent border border-border/60 rounded px-1.5 py-0.5"
                value={p.hour_utc != null ? toLocal(p.hour_utc) : 9}
                onChange={(e) => save.mutate({ kind: p.kind, enabled: true, hour_utc: toUtc(Number(e.target.value)) })}
              >
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00`}</option>)}
              </select>
            )}
            <Switch
              checked={p.enabled}
              disabled={viewAs}
              onCheckedChange={(on) => save.mutate({
                kind: p.kind, enabled: on,
                // The recap needs an hour: the user's 9:00 until they pick another.
                hour_utc: p.kind === "dailyRecap" ? (p.hour_utc ?? toUtc(9)) : undefined,
              })}
            />
          </div>
        </div>
      ))}

      {/* The timed nudges (webpush/timed.go, 10-31): one a day at most, at their usual hour. */}
      <p className="text-xs text-muted-foreground">{t("notif.timed.note")}</p>
      <div className="flex items-center justify-between gap-3 pt-2 border-t border-border/40">
        <span>{t("notif.sound")}</span>
        <Switch checked={sound} onCheckedChange={(on) => { setSoundOn(on); setSound(on); if (on) chime("small"); }} />
      </div>
      {/* PostHog on this device (lib/posthog.ts, 2026-10-22): figures and typing are never recorded. */}
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0">
          <span className="block">{t("notif.usage")}</span>
          <span className="block text-xs text-muted-foreground">{t("notif.usageHint")}</span>
        </span>
        <Switch checked={usage} onCheckedChange={(on) => { setPostHogOptOut(!on); setUsage(on); }} aria-label={t("notif.usage")} />
      </div>
    </div>
  );
}
